import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@foundation/altzero_base/base/components/ui/dialog';
import { Button } from '@foundation/altzero_base/base/components/ui/button';
import { Input } from '@foundation/altzero_base/base/components/ui/input';
import { Label } from '@foundation/altzero_base/base/components/ui/label';
import type { Venue, CreateVenuePayload, SPORT_OPTIONS, AU_STATE_OPTIONS } from '../types/venue.types';
import { theme } from '../../../theme';

type VenueFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateVenuePayload) => Promise<void>;
  venue?: Venue | null;
};

const SPORTS: (typeof SPORT_OPTIONS)[number][] = [
  'Rugby', 'AFL', 'Basketball', 'Soccer', 'Skateboarding', 'MTB', 'BMX', 'Other',
];

const STATES: (typeof AU_STATE_OPTIONS)[number][] = [
  'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT',
];

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export const VenueForm = ({ open, onClose, onSubmit, venue }: VenueFormProps) => {
  const isEditing = !!venue;

  const [venueName, setVenueName] = useState(venue?.venue_name ?? '');
  const [venueSlug, setVenueSlug] = useState(venue?.venue_slug ?? '');
  const [sport, setSport] = useState(venue?.sport ?? '');
  const [addressLine1, setAddressLine1] = useState(venue?.address?.line1 ?? '');
  const [addressLine2, setAddressLine2] = useState(venue?.address?.line2 ?? '');
  const [city, setCity] = useState(venue?.address?.city ?? '');
  const [state, setState] = useState(venue?.address?.state ?? '');
  const [postcode, setPostcode] = useState(venue?.address?.postcode ?? '');
  const [lat, setLat] = useState(venue?.geolocation?.lat?.toString() ?? '');
  const [lng, setLng] = useState(venue?.geolocation?.lng?.toString() ?? '');
  const [contactPhone, setContactPhone] = useState(venue?.contact_phone ?? '');
  const [contactEmail, setContactEmail] = useState(venue?.contact_email ?? '');
  const [nearestHospital, setNearestHospital] = useState(venue?.emergency_info?.nearestHospital ?? '');
  const [emergencyNotes, setEmergencyNotes] = useState(venue?.emergency_info?.emergencyNotes ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setVenueName(name);
    if (!isEditing) {
      setVenueSlug(generateSlug(name));
    }
  };

  const handleSubmit = async () => {
    if (!venueName.trim()) return;

    setIsSubmitting(true);
    try {
      const payload: CreateVenuePayload = {
        venue_name: venueName.trim(),
        venue_slug: venueSlug.trim() || generateSlug(venueName),
        sport: sport || undefined,
        address: {
          line1: addressLine1 || undefined,
          line2: addressLine2 || undefined,
          city: city || undefined,
          state: state || undefined,
          postcode: postcode || undefined,
          country: 'Australia',
        },
        geolocation: lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined,
        contact_phone: contactPhone || undefined,
        contact_email: contactEmail || undefined,
        emergency_info: {
          nearestHospital: nearestHospital || undefined,
          emergencyNotes: emergencyNotes || undefined,
        },
      };

      await onSubmit(payload);
      onClose();
    } catch (error) {
      console.error('Venue form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="  max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Venue' : 'Create Venue'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Venue Name */}
          <div>
            <Label htmlFor="venue-name">Venue Name *</Label>
            <Input
              id="venue-name"
              value={venueName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Willowbank Rugby Club"
            />
          </div>

          {/* Venue Slug */}
          <div>
            <Label htmlFor="venue-slug">URL Slug</Label>
            <Input
              id="venue-slug"
              value={venueSlug}
              onChange={(e) => setVenueSlug(e.target.value)}
              placeholder="auto-generated-from-name"
            />
            <p className={`text-xs ${theme.text.muted} mt-1`}>
              Used in QR URL: /qr/{venueSlug || 'your-slug'}
            </p>
          </div>

          {/* Sport */}
          <div>
            <Label htmlFor="sport">Sport</Label>
            <select
              id="sport"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className={theme.input.select}
            >
              <option value="">Select sport...</option>
              {SPORTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Address Line 1"
            />
            <Input
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Address Line 2"
            />
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
              />
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={`h-10 ${theme.input.select}`}
              >
                <option value="">State</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <Input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="Postcode"
              />
            </div>
          </div>

          {/* Geolocation */}
          <div>
            <Label>Venue Coordinates (optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                step="any"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="Latitude"
              />
              <Input
                type="number"
                step="any"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="Longitude"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="contact-phone">Contact Phone</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="0412 345 678"
              />
            </div>
            <div>
              <Label htmlFor="contact-email">Contact Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="club@example.com"
              />
            </div>
          </div>

          {/* Emergency Info */}
          <div className="space-y-2">
            <Label>Emergency Information</Label>
            <Input
              value={nearestHospital}
              onChange={(e) => setNearestHospital(e.target.value)}
              placeholder="Nearest Hospital"
            />
            <textarea
              value={emergencyNotes}
              onChange={(e) => setEmergencyNotes(e.target.value)}
              placeholder="Additional emergency notes..."
              className={theme.input.textareaSm}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !venueName.trim()}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Create Venue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
